import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  FileText,
  Eye,
  Heart,
  Share2,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { format, startOfWeek, addDays, isToday } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import DashboardLayout from '../components/DashboardLayout';
import AnalyticsHealthWidget from '../components/AnalyticsHealthWidget';
import OnboardingTour from '../components/OnboardingTour';
import { api } from '../context/AuthContext';

const PLATFORM_ICONS = {
  facebook: '📘',
  instagram: '📸',
  twitter: '🐦',
  linkedin: '💼',
  tiktok: '🎵',
  youtube: '📺',
  pinterest: '📌',
  threads: '🧵',
  bluesky: '🦋'
};

const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
  approved: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Approved' },
  scheduled: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Scheduled' },
  published: { bg: 'bg-green-100', text: 'text-green-700', label: 'Published' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' }
};

const DashboardPage = () => {
  const [calendars, setCalendars] = useState([]);
  const [posts, setPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!loading) {
      const tourCompleted = localStorage.getItem('stellisocial_tour_completed');
      if (!tourCompleted) {
        const timer = setTimeout(() => setShowTour(true), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [loading]);

  const fetchDashboardData = async () => {
    try {
      const [calendarsRes, postsRes, notificationsRes, analyticsRes] = await Promise.all([
        api.get('/calendars'),
        api.get('/posts'),
        api.get('/notifications?unread_only=true'),
        api.get('/analytics/overview')
      ]);

      setCalendars(calendarsRes.data);
      setPosts(postsRes.data);
      setNotifications(notificationsRes.data.slice(0, 5));
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingPosts = posts
    .filter(p => p.status === 'scheduled' && p.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    .slice(0, 5);

  const pendingApprovals = posts.filter(p => p.status === 'pending_approval');
  const drafts = posts.filter(p => p.status === 'draft');

  const weekDays = [...Array(7)].map((_, i) => {
    const date = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i);
    const dayPosts = posts.filter(p => {
      if (!p.scheduled_at) return false;
      return format(new Date(p.scheduled_at), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
    });
    return { date, posts: dayPosts };
  });

  const stats = [
    {
      title: 'Total Posts',
      value: posts.length,
      icon: FileText,
      change: '+12%',
      color: 'bg-blue-500'
    },
    {
      title: 'Scheduled',
      value: posts.filter(p => p.status === 'scheduled').length,
      icon: Clock,
      change: '+5%',
      color: 'bg-emerald-500'
    },
    {
      title: 'Published',
      value: posts.filter(p => p.status === 'published').length,
      icon: CheckCircle,
      change: '+18%',
      color: 'bg-green-500'
    },
    {
      title: 'Engagement',
      value: analytics?.summary?.total_engagement?.toLocaleString() || '0',
      icon: Heart,
      change: '+23%',
      color: 'bg-pink-500'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
            <p className="text-slate-600 mt-1">Welcome back! Here's what's happening with your content.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/ideas">
              <Button variant="outline" className="border-slate-200">
                <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
                Get Ideas
              </Button>
            </Link>
            <Link to="/posts/new">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="dashboard-create-post-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Post
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-tour="stats-grid">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-5 w-5 text-white" />
                    </div>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 font-medium">
                      {stat.change}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Week Overview */}
          <Card className="lg:col-span-2 border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">This Week</CardTitle>
              <Link to="/calendars" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                View Calendar <ArrowRight className="h-4 w-4" />
              </Link>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map((day, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg border ${
                      isToday(day.date)
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-100 bg-slate-50'
                    }`}
                  >
                    <p className={`text-xs font-medium ${isToday(day.date) ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {format(day.date, 'EEE')}
                    </p>
                    <p className={`text-lg font-bold ${isToday(day.date) ? 'text-emerald-600' : 'text-slate-900'}`}>
                      {format(day.date, 'd')}
                    </p>
                    <div className="mt-2 space-y-1">
                      {day.posts.slice(0, 2).map((post, j) => (
                        <div
                          key={j}
                          className="text-xs px-2 py-1 rounded bg-white border border-slate-200 truncate"
                          title={post.content}
                        >
                          {post.platforms.map(p => PLATFORM_ICONS[p] || '📱').join('')}
                        </div>
                      ))}
                      {day.posts.length > 2 && (
                        <p className="text-xs text-slate-500 text-center">
                          +{day.posts.length - 2} more
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Calendars */}
          <Card className="border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Calendars</CardTitle>
              <Link to="/calendars/new">
                <Button variant="ghost" size="sm" className="text-emerald-600">
                  <Plus className="h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {calendars.length === 0 ? (
                <div className="text-center py-6">
                  <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No calendars yet</p>
                  <Link to="/calendars/new">
                    <Button variant="link" className="text-emerald-600">
                      Create your first calendar
                    </Button>
                  </Link>
                </div>
              ) : (
                calendars.slice(0, 4).map((calendar) => (
                  <Link
                    key={calendar.calendar_id}
                    to={`/calendars/${calendar.calendar_id}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-colors"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: calendar.brand_color }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{calendar.name}</p>
                      <p className="text-xs text-slate-500">{calendar.post_count || 0} posts</p>
                    </div>
                  </Link>
                ))
              )}
              {calendars.length > 4 && (
                <Link to="/calendars" className="block text-center text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                  View all {calendars.length} calendars
                </Link>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming Posts */}
          <Card className="border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Upcoming Posts</CardTitle>
              <Link to="/posts?status=scheduled" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                View all
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {upcomingPosts.length === 0 ? (
                <div className="text-center py-6">
                  <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No scheduled posts</p>
                  <Link to="/posts/new">
                    <Button variant="link" className="text-emerald-600">
                      Schedule your first post
                    </Button>
                  </Link>
                </div>
              ) : (
                upcomingPosts.map((post) => (
                  <Link
                    key={post.post_id}
                    to={`/posts/${post.post_id}`}
                    className="block p-3 rounded-lg border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 line-clamp-2">{post.content}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-slate-500">
                            {format(new Date(post.scheduled_at), 'MMM d, h:mm a')}
                          </span>
                          <span className="text-sm">{post.platforms.map(p => PLATFORM_ICONS[p] || '📱').join(' ')}</span>
                        </div>
                      </div>
                      <Badge className={`${STATUS_STYLES[post.status].bg} ${STATUS_STYLES[post.status].text}`}>
                        {STATUS_STYLES[post.status].label}
                      </Badge>
                    </div>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>

          {/* Pending Approvals & Drafts */}
          <Card className="border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Needs Attention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pending Approvals */}
              {pendingApprovals.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-slate-900">
                      {pendingApprovals.length} pending approval{pendingApprovals.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {pendingApprovals.slice(0, 2).map((post) => (
                      <Link
                        key={post.post_id}
                        to={`/posts/${post.post_id}`}
                        className="block p-2 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                      >
                        <p className="text-sm text-amber-900 line-clamp-1">{post.content}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Drafts */}
              {drafts.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-900">
                      {drafts.length} draft{drafts.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {drafts.slice(0, 2).map((post) => (
                      <Link
                        key={post.post_id}
                        to={`/posts/${post.post_id}`}
                        className="block p-2 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors"
                      >
                        <p className="text-sm text-slate-700 line-clamp-1">{post.content}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {pendingApprovals.length === 0 && drafts.length === 0 && (
                <div className="text-center py-6">
                  <CheckCircle className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">All caught up!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Performance Overview */}
        {analytics && (
          <Card className="border-slate-100">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Performance Overview</CardTitle>
              <Link to="/analytics" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                View Analytics <ArrowRight className="h-4 w-4" />
              </Link>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Total Reach</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {analytics.summary?.total_reach?.toLocaleString() || '0'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Impressions</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {analytics.summary?.total_impressions?.toLocaleString() || '0'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Heart className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Engagement</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {analytics.summary?.total_engagement?.toLocaleString() || '0'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Share2 className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Engagement Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {analytics.summary?.engagement_rate || '0'}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Health Widget */}
        <AnalyticsHealthWidget />
      </div>
      {showTour && (
        <OnboardingTour onComplete={() => setShowTour(false)} />
      )}
    </DashboardLayout>
  );
};

export default DashboardPage;
